/**
 * Event List Component
 *
 * Displays list of scheduled events/workouts
 * Dense, readable format matching Fidelity style
 */

import './EventList.css';

interface ScheduleEvent {
  id: string;
  title: string;
  type: 'workout' | 'task' | 'appointment' | 'other';
  date: Date;
  duration?: string;
  sets?: number;
}

interface EventListProps {
  events: ScheduleEvent[];
  onEventClick?: (event: ScheduleEvent) => void;
}

function formatDate(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    });
  }
}

export function EventList({ events, onEventClick }: EventListProps): JSX.Element {
  if (events.length === 0) {
    return (
      <div className="event-list event-list--empty">
        <p className="event-list__empty-text">No events scheduled</p>
      </div>
    );
  }

  const handleClick = (event: ScheduleEvent): void => {
    if (onEventClick) {
      onEventClick(event);
    } else {
      console.log('Event clicked:', event.id);
    }
  };

  return (
    <div className="event-list">
      {events.map((event) => (
        <div
          key={event.id}
          className="event-card"
          role="button"
          tabIndex={0}
          onClick={() => handleClick(event)}
          onKeyDown={(e) => e.key === 'Enter' && handleClick(event)}
        >
          <div className="event-card__icon">
            {event.type === 'workout' && '💪'}
            {event.type === 'task' && '✓'}
            {event.type === 'appointment' && '📅'}
          </div>

          <div className="event-card__content">
            <div className="event-card__header">
              <h3 className="event-card__title">{event.title}</h3>
              <span className="event-card__date">{formatDate(event.date)}</span>
            </div>

            {(event.duration || event.sets) && (
              <div className="event-card__details">
                {event.duration && (
                  <span className="event-card__detail">⏱ {event.duration}</span>
                )}
                {event.sets && (
                  <span className="event-card__detail">📊 {event.sets} sets</span>
                )}
              </div>
            )}
          </div>

          <span className="event-card__action" aria-hidden="true">
            →
          </span>
        </div>
      ))}
    </div>
  );
}
